import { ThemeEvents } from '@theme/events';

const acceptedSignatures = new Map();

class ReservationCheckoutControl extends HTMLElement {
  static observedAttributes = ['data-reservation-signature'];

  #acceptedSignature = null;
  #checkbox = null;
  #checkoutButton = null;
  #cartForm = null;

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#bindControls();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#unbindControls();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-reservation-signature' || oldValue === newValue || oldValue === null) return;

    this.#acceptedSignature = null;
    acceptedSignatures.delete(this.#stateKey);
    queueMicrotask(() => this.#bindControls());
  }

  #handleCartUpdate = () => {
    queueMicrotask(() => this.#bindControls());
  };

  #bindControls() {
    this.#unbindControls();

    this.#checkbox = this.querySelector('[data-reservation-acceptance]');
    this.#checkoutButton = this.querySelector('[name="checkout"]');
    this.#cartForm = this.#checkoutButton?.form || null;

    if (!this.dataset.reservationSignature) {
      acceptedSignatures.delete(this.#stateKey);
    } else {
      this.#acceptedSignature = acceptedSignatures.get(this.#stateKey) || this.#acceptedSignature;
    }

    if (!(this.#checkbox instanceof HTMLInputElement) || !(this.#checkoutButton instanceof HTMLButtonElement)) return;

    const isAccepted = this.#acceptedSignature === this.dataset.reservationSignature;
    this.#checkbox.checked = isAccepted;
    this.#checkbox.addEventListener('change', this.#handleAcceptanceChange);
    this.#cartForm?.addEventListener('submit', this.#handleSubmit, true);
    this.#syncCheckoutState();
  }

  #unbindControls() {
    this.#checkbox?.removeEventListener('change', this.#handleAcceptanceChange);
    this.#cartForm?.removeEventListener('submit', this.#handleSubmit, true);
  }

  #handleAcceptanceChange = () => {
    this.#acceptedSignature = this.#checkbox?.checked ? this.dataset.reservationSignature || null : null;
    if (this.#acceptedSignature) {
      acceptedSignatures.set(this.#stateKey, this.#acceptedSignature);
    } else {
      acceptedSignatures.delete(this.#stateKey);
    }
    this.#syncCheckoutState();
  };

  #handleSubmit = (event) => {
    if (event.submitter && event.submitter !== this.#checkoutButton) return;
    if (this.#checkbox?.checked) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#showError();
  };

  #syncCheckoutState() {
    if (!(this.#checkbox instanceof HTMLInputElement) || !(this.#checkoutButton instanceof HTMLButtonElement)) return;

    const isAccepted = this.#checkbox.checked;
    this.#checkoutButton.disabled = !isAccepted;
    this.#checkoutButton.setAttribute('aria-disabled', String(!isAccepted));
    this.#checkbox.setAttribute('aria-invalid', String(!isAccepted));

    const error = this.querySelector('[data-reservation-error]');
    if (error instanceof HTMLElement && isAccepted) error.hidden = true;
  }

  #showError() {
    const notice = this.querySelector('[data-reservation-notice]');
    const error = this.querySelector('[data-reservation-error]');

    if (error instanceof HTMLElement) error.hidden = false;
    if (notice instanceof HTMLElement) notice.focus();
  }

  get #stateKey() {
    return this.dataset.reservationControlId || 'cart';
  }
}

if (!customElements.get('reservation-checkout-control')) {
  customElements.define('reservation-checkout-control', ReservationCheckoutControl);
}
